import React, { useEffect, useState } from 'react'
import { CustomModal } from 'components'
import dayjs from 'dayjs'
import axiosInstance from 'utils/axiosConfig'
import { toastError } from 'utils/toastify'
import { errorMessage } from 'constant/const'

function BatchAnalysisModal({ isOpenNewVideoModal, onClose, selectedChannelID }) {
   const [loading, setLoading] = useState(false)
   const [videoData, setVideoData] = useState([])
   const [count, setCount] = useState(0)
   const [currentPage, setCurrentPage] = useState(1)
   const perPage = 10

   useEffect(() => {
      if (isOpenNewVideoModal) {
         fetchBatchData(currentPage)
      }
   }, [isOpenNewVideoModal, currentPage])

   const fetchBatchData = async (page = 1) => {
      try {
         setLoading(true)
         const response = await axiosInstance.get('yt/get_batch_analysis_list', {
            params: {
               channel_id: selectedChannelID,
               page: page
            }
         })
         setVideoData(response.data?.data || [])
         setCount(response.data?.count || 0)
      } catch (error) {
         toastError(errorMessage)
         console.error('Error fetching batch analysis:', error)
         setVideoData([])
      } finally {
         setLoading(false)
      }
   }

   return (
      <CustomModal
         show={isOpenNewVideoModal}
         title={'Batch Analysis Status'}
         closeModal={onClose}
         size={'xl'}
         scrollable
         centered>
         <div className="pt-0 batch-table">
            <table className="table table-hover">
               <thead>
                  <tr>
                     <th>#</th>
                     <th>Batch ID</th>
                     <th>Channel Name</th>
                     <th>Video Names</th>
                     <th>Date</th>
                     <th>Status</th>
                  </tr>
               </thead>
               <tbody>
                  {loading ? (
                     <tr>
                        <td colSpan="6" className="text-center py-4">
                           Loading...
                        </td>
                     </tr>
                  ) : videoData.length ? (
                     videoData.map((item, index) => (
                        <tr key={index}>
                           <th scope="row">{(currentPage - 1) * perPage + index + 1}</th>
                           <td>{item?.batch_id}</td>
                           <td>{item?.channel_name}</td>
                           <td>
                              <ul className="mb-0 ps-3">
                                 {item.video_status.map((video, idx) => (
                                    <li key={idx} className="mb-1">
                                       {video?.video_name}{' '}
                                       <span title={video?.status ? 'Completed' : 'Pending'}>
                                          {video?.status ? '✔️' : '⏳'}
                                       </span>
                                    </li>
                                 ))}
                              </ul>
                           </td>
                           <td>{dayjs(item?.date).format('DD MMM YYYY')}</td>
                           <td className='text-capitalize'>{item?.status}</td>
                        </tr>
                     ))
                  ) : (
                     <tr>
                        <td colSpan="6" className="text-center py-4">
                           No data available
                        </td>
                     </tr>
                  )}
               </tbody>
            </table>

            {!loading && count > perPage && (
               <Pagination
                  currentPage={currentPage}
                  totalCount={count}
                  perPage={perPage}
                  onPageChange={(page) => setCurrentPage(page)}
               />
            )}
         </div>
      </CustomModal>
   )
}

export default BatchAnalysisModal

// ---------------- Pagination Component ----------------
const Pagination = ({ currentPage, totalCount, perPage, onPageChange }) => {
   const totalPages = Math.ceil(totalCount / perPage)

   const generatePages = () => {
      const pages = []

      if (totalPages <= 6) {
         for (let i = 1; i <= totalPages; i++) pages.push(i)
      } else {
         pages.push(1)
         if (currentPage > 3) pages.push('...')
         const start = Math.max(2, currentPage - 1)
         const end = Math.min(totalPages - 1, currentPage + 1)

         for (let i = start; i <= end; i++) pages.push(i)

         if (currentPage < totalPages - 2) pages.push('...')
         pages.push(totalPages)
      }

      return pages
   }

   return (
      <div className="pagination-wrapper">
         <div>
            <button
               onClick={() => onPageChange(currentPage - 1)}
               disabled={currentPage === 1}
               className="page-btn">
               &lt;
            </button>
         </div>

         <div className="page-item">
            {generatePages().map((page, idx) => (
               <button
                  key={idx}
                  onClick={() => typeof page === 'number' && onPageChange(page)}
                  disabled={page === '...'}
                  className={`page-btn ${currentPage === page ? 'active' : ''}`}>
                  {page}
               </button>
            ))}
         </div>

         <div>
            <button
               onClick={() => onPageChange(currentPage + 1)}
               disabled={currentPage === totalPages}
               className="page-btn">
               &gt;
            </button>
         </div>
      </div>
   )
}
